/**
 * AI Marketing Assistant — kartu pemicu di Dashboard Marketing + panel chat.
 *
 * Brief owner 31 Agu 2026: "Tambahkan tombol AI Marketing Assistant di
 * Dashboard. UI berupa chat interface sederhana seperti chatbot."
 *
 * KENAPA SATU BERKAS (kartu + panel), BUKAN HALAMAN BER-RUTE SENDIRI
 * ------------------------------------------------------------------
 * Kartunya harus "terlihat jelas di Dashboard" dan chatnya dipakai sambil
 * melihat angka dashboard — halaman terpisah justru memaksa pengguna
 * bolak-balik. Sebagai komponen yang diimpor MarketingDashboard, berkas ini
 * juga ikut dilewati compiler saat `yarn build`, jadi ESLint benar-benar
 * memeriksanya (halaman yang belum ter-route TIDAK di-lint — lihat CLAUDE.md).
 *
 * KENAPA TIDAK MEMAKAI `Modal` DARI `_shared.jsx`
 * -----------------------------------------------
 * `Modal` memasang `useModalGuard` yang menahan penutupan begitu ada isian
 * belum tersimpan. Di panel chat SELALU ada isian (kotak pertanyaan), jadi
 * setiap kali panel ditutup pengguna akan disodori konfirmasi "buang isian?"
 * yang tidak ada artinya di sini — percakapan chat memang tidak disimpan.
 *
 * GERBANG IZIN
 * ------------
 * `bolehPakaiAI(user, can)` dari `lib/izinAI.js` — cermin persis
 * `backend/ai_akses.py`. JANGAN diganti pemanggilan `can()` mentah atas kunci
 * izin AI: `defaultCan()` menolak role finance_* untuk modul non-finance
 * padahal backend meloloskannya, jadi tombolnya hilang untuk orang yang
 * justru berhak (dan `backend/tests/test_gerbang_fitur_ai.py` menolaknya).
 * Backend tetap menolak sendiri (aturan #2 CLAUDE.md) — gerbang di sini hanya
 * supaya tidak ada tombol yang ujungnya 403 (aturan #3).
 *
 * PERCAKAPAN TIDAK DISIMPAN
 * -------------------------
 * Riwayat hidup di state React saja dan ikut hilang saat panel/tab ditutup —
 * sesuai brief. Yang dikirim balik ke server hanya untuk menyambung konteks;
 * SELURUH angka dirakit ulang di server tiap giliran, jadi riwayat yang
 * diubah-ubah di sisi klien tidak bisa memalsukan data.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Sparkles, Send, Loader2, Bot, User as UserIcon, X, Wand2,
  Database, AlertTriangle, RotateCcw,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import { bolehPakaiAI } from "@/lib/izinAI";

// Panggilan LLM biasa memakan lebih dari 30 detik (timeout bawaan `api`),
// sementara `ai_provider` di backend sendiri menunggu sampai 120 detik.
// Tanpa penyesuaian ini layar menyerah lebih dulu daripada servernya dan
// menampilkan "gagal" untuk jawaban yang sebetulnya sedang dalam perjalanan.
const TIMEOUT_CHAT_MS = 120000;

const PESAN_GAGAL_UMUM =
  "Layanan AI sedang tidak bisa dihubungi. Coba lagi beberapa saat lagi.";

/** Pesan galat yang bisa dibaca orang — detail backend dipakai kalau ada. */
function pesanGalat(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (e?.code === "ECONNABORTED") {
    return "Layanan AI tidak menjawab sampai batas waktu. Coba pertanyaan yang lebih ringkas.";
  }
  return PESAN_GAGAL_UMUM;
}

export default function AiAsisten() {
  const { user, can } = useAuth();
  const boleh = bolehPakaiAI(user, can);
  const [buka, setBuka] = useState(false);

  // Early-return SESUDAH seluruh hook dipanggil — aturan hooks React.
  if (!boleh) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setBuka(true)}
        data-testid="mkt-ai-asisten-buka"
        className="w-full text-left bg-gradient-to-br from-emerald-600 to-emerald-800 text-white
                   border border-emerald-700 rounded-xl shadow-sm p-5 hover:from-emerald-500
                   hover:to-emerald-700 transition-colors"
      >
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg bg-white/15 grid place-items-center flex-shrink-0">
            <Sparkles className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <p className="font-display text-base font-bold">AI Marketing Assistant</p>
            <p className="text-xs text-emerald-50/90 mt-1">
              Tanya apa saja soal performa marketing, KOL &amp; Engagement Rate, konten, dan
              campaign — dijawab dari data marketing yang benar-benar tercatat.
            </p>
            <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-medium bg-white/15 rounded-lg px-3 py-1.5">
              <Bot className="w-3.5 h-3.5" /> Buka Chat
            </span>
          </div>
        </div>
      </button>

      {buka && <PanelChat onClose={() => setBuka(false)} />}
    </>
  );
}

function PanelChat({ onClose }) {
  const [pesanList, setPesanList] = useState([]);   // {peran, isi, galat?}
  const [input, setInput] = useState("");
  const [mengirim, setMengirim] = useState(false);
  const [lihatData, setLihatData] = useState(false);
  const listRef = useRef(null);

  const { data: kemampuan } = useDataQuery(
    ["marketing", "ai-asisten-kemampuan"],
    () => api.get("/marketing/ai-asisten/kemampuan").then((r) => r.data),
  );
  const { data: konteks, loading: loadingKonteks } = useDataQuery(
    ["marketing", "ai-asisten-konteks"],
    () => api.get("/marketing/ai-asisten/konteks").then((r) => r.data),
    { enabled: lihatData },
  );

  // Escape menutup panel. Tidak ada penjaga "isian belum tersimpan": chat
  // memang tidak disimpan, jadi konfirmasi apa pun di sini cuma mengganggu.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [pesanList, mengirim]);

  const kirim = useCallback(async (teks) => {
    const isi = (teks ?? "").trim();
    if (!isi || mengirim) return;
    setInput("");
    setMengirim(true);
    // Riwayat yang dikirim adalah percakapan SEBELUM pesan ini — server
    // memotongnya lagi sendiri, di sini cukup mengirim yang bukan galat
    // (bubble galat bukan jawaban AI, memasukkannya akan membingungkan model).
    const riwayat = pesanList
      .filter((m) => !m.galat)
      .map((m) => ({ role: m.peran, isi: m.isi }));
    setPesanList((m) => [...m, { peran: "user", isi }]);
    try {
      const { data } = await api.post(
        "/marketing/ai-asisten/chat",
        { pesan: isi, riwayat },
        { timeout: TIMEOUT_CHAT_MS },
      );
      setPesanList((m) => [...m, {
        peran: "assistant",
        isi: data?.jawaban || PESAN_GAGAL_UMUM,
        bagian: data?.bagian_data,
        tanpaIzin: data?.bagian_tanpa_izin,
      }]);
    } catch (e) {
      setPesanList((m) => [...m, { peran: "assistant", isi: pesanGalat(e), galat: true }]);
    }
    setMengirim(false);
  }, [mengirim, pesanList]);

  const saran = kemampuan?.items;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-stretch justify-end"
      onClick={onClose}
      data-testid="mkt-ai-asisten-panel"
    >
      <div
        className="bg-white w-full sm:max-w-xl h-full flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Kepala */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-700 text-white grid place-items-center flex-shrink-0">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="font-display font-bold text-sm text-slate-900">AI Marketing Assistant</p>
              <p className="text-[11px] text-slate-500 truncate">
                Jawaban dirakit dari data marketing di server — percakapan tidak disimpan.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {pesanList.length > 0 && (
              <button type="button" onClick={() => setPesanList([])}
                      data-testid="mkt-ai-asisten-reset" title="Mulai percakapan baru"
                      className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
                <RotateCcw className="w-4 h-4" />
              </button>
            )}
            <button type="button" onClick={onClose} data-testid="mkt-ai-asisten-tutup"
                    className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Percakapan */}
        <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {pesanList.length === 0 ? (
            <div className="py-4">
              <p className="text-sm text-slate-600 mb-1">
                Mau tahu apa dari data marketing hari ini?
              </p>
              <p className="text-xs text-slate-400 mb-4">
                Pilih salah satu, atau ketik pertanyaanmu sendiri di bawah.
              </p>
              <div className="grid grid-cols-1 gap-2">
                {(saran || []).map((k, i) => (
                  <button key={k.kunci} type="button" onClick={() => kirim(k.contoh)}
                          data-testid={`mkt-ai-asisten-saran-${i}`}
                          className="text-left px-3.5 py-2.5 rounded-lg border border-slate-200
                                     hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors">
                    <span className="block text-[11px] font-semibold text-emerald-700">
                      <Wand2 className="w-3 h-3 inline mr-1" />{k.label}
                    </span>
                    <span className="block text-xs text-slate-600 mt-0.5">{k.contoh}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            pesanList.map((m, i) => <Gelembung key={i} pesan={m} />)
          )}
          {mengirim && (
            <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="mkt-ai-asisten-menunggu">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> AI sedang membaca data marketing…
            </div>
          )}
        </div>

        {/* Data yang dibaca AI — supaya jawabannya bisa diperiksa, bukan dipercaya begitu saja */}
        <div className="border-t border-slate-100">
          <button type="button" onClick={() => setLihatData((v) => !v)}
                  data-testid="mkt-ai-asisten-lihat-data"
                  className="w-full flex items-center gap-1.5 px-5 py-2 text-[11px] text-slate-500 hover:bg-slate-50">
            <Database className="w-3.5 h-3.5" />
            {lihatData ? "Sembunyikan data yang dibaca AI" : "Lihat data yang dibaca AI"}
          </button>
          {lihatData && (
            <div className="px-5 pb-3">
              {loadingKonteks ? (
                <p className="text-[11px] text-slate-400">Memuat…</p>
              ) : (
                <>
                  <p className="text-[11px] text-slate-500 mb-1">
                    Bagian tersedia: {(konteks?.bagian_tersedia || []).join(", ") || "-"}
                    {konteks?.bagian_tanpa_izin?.length
                      ? ` · Tanpa izin: ${konteks.bagian_tanpa_izin.join(", ")}`
                      : ""}
                  </p>
                  <pre className="text-[10px] bg-slate-50 border border-slate-200 rounded-lg p-2
                                  max-h-40 overflow-auto whitespace-pre-wrap break-all text-slate-600"
                       data-testid="mkt-ai-asisten-konteks-json">
                    {JSON.stringify(konteks || {}, null, 1)}
                  </pre>
                </>
              )}
            </div>
          )}
        </div>

        {/* Kotak kirim */}
        <form
          onSubmit={(e) => { e.preventDefault(); kirim(input); }}
          className="border-t border-slate-100 p-3 flex gap-2"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={mengirim}
            maxLength={2000}
            placeholder="Contoh: KOL mana yang paling layak dilanjutkan bulan depan?"
            data-testid="mkt-ai-asisten-input"
            className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm
                       focus:outline-none focus:ring-2 focus:ring-emerald-500/40 disabled:bg-slate-50"
          />
          <button type="submit" disabled={mengirim || !input.trim()}
                  data-testid="mkt-ai-asisten-kirim"
                  className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50
                             text-white text-sm font-medium inline-flex items-center gap-1.5">
            {mengirim ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Kirim
          </button>
        </form>
      </div>
    </div>
  );
}

function Gelembung({ pesan }) {
  const dariUser = pesan.peran === "user";
  const adaBagianTertutup = !dariUser && !pesan.galat && (pesan.tanpaIzin || []).length > 0;
  return (
    <div className={`flex gap-2.5 ${dariUser ? "flex-row-reverse" : ""}`}>
      <div className={`w-7 h-7 rounded-full grid place-items-center flex-shrink-0 ${
        dariUser ? "bg-slate-900 text-white"
          : pesan.galat ? "bg-rose-100 text-rose-600"
            : "bg-gradient-to-br from-emerald-400 to-emerald-700 text-white"}`}>
        {dariUser ? <UserIcon className="w-3.5 h-3.5" />
          : pesan.galat ? <AlertTriangle className="w-3.5 h-3.5" />
            : <Bot className="w-3.5 h-3.5" />}
      </div>
      <div className={`max-w-[82%] rounded-xl px-3.5 py-2.5 ${
        dariUser ? "bg-emerald-600 text-white"
          : pesan.galat ? "bg-rose-50 text-rose-800 border border-rose-200"
            : "bg-slate-50 text-slate-900 border border-slate-100"}`}>
        <p className="text-sm whitespace-pre-wrap leading-relaxed">{pesan.isi}</p>
        {adaBagianTertutup && (
          <p className="text-[10px] text-slate-500 mt-1.5">
            Tidak ikut dianalisis (akunmu belum punya izinnya): {pesan.tanpaIzin.join(", ")}.
          </p>
        )}
      </div>
    </div>
  );
}
